/**
 * useLiveSchedule — the ticker that makes per-agent live schedules REAL.
 *
 * Mounted once at the app root (independent of the global autonomy
 * switch): while the app is open, a 60s check evaluates the pure due
 * logic in liveSchedule.ts and runs every due agent whose own
 * `liveSchedule` flag is on, via the same governed `runAgentOnce` the
 * "Run now" button uses. Safe-by-design: runs only QUEUE proposals into
 * the approvals inbox — they never write CRM data directly — which is
 * why this can tick without the global toggle. Each run stamps
 * lastRunAt/nextRunAt on the agent record (cards update live) and
 * appends an "Argo did this" feed entry with the run's real counts.
 *
 * The loop self-schedules so ticks never overlap, with a closure-local
 * cancel flag per effect run (StrictMode-safe, same pattern as
 * useAgentScheduler).
 */

import { useEffect, useState } from 'react';
import { listAgents, runAgentOnce } from '../db/agents';
import { dueLiveAgents } from './liveSchedule';
import { automationsTick } from './automations';

/** One live-scheduled run the ticker just fired. */
export interface LiveRunEvent {
  agentId: string;
  agentName: string;
  at: string;
}

/**
 * One ticker pass: run EVERY due live-scheduled agent (most-overdue
 * first). Exported so it can be unit-tested and driven manually. A bad
 * individual run is swallowed so it can't stop the rest of the sweep.
 */
export async function liveScheduleTick(now = Date.now()): Promise<LiveRunEvent[]> {
  const agents = await listAgents();
  const due = dueLiveAgents(agents, now);
  const events: LiveRunEvent[] = [];
  for (const agent of due) {
    try {
      const ran = await runAgentOnce(agent.id, { trigger: 'schedule' });
      events.push({
        agentId: ran.id,
        agentName: ran.name,
        at: ran.lastRunAt ?? new Date(now).toISOString(),
      });
    } catch {
      /* swallow — one failing agent must not stop the sweep */
    }
  }
  // Heartbeat's scoped automations ride the SAME tick (no second
  // scheduler): each enabled automation, when due on its own cadence,
  // generates drafts and QUEUES approvals + a feed entry — never a direct
  // CRM/outbox write. Best-effort: automations must not break agent runs.
  try {
    await automationsTick(now);
  } catch {
    /* swallow — a bad automation sweep must not kill the tick */
  }
  return events;
}

/**
 * Drive the live-schedule loop while mounted. Always on (the per-agent
 * flag is the opt-in, not a global switch); `intervalMs` defaults to the
 * 60s check the ticking contract promises.
 */
export function useLiveSchedule(intervalMs = 60_000): { lastTickAt: string | null } {
  const [lastTickAt, setLastTickAt] = useState<string | null>(null);

  useEffect(() => {
    // Closure-local cancel flag (StrictMode-safe): each effect run's
    // cleanup is authoritative for the loop it started.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const loop = async () => {
      if (cancelled) return;
      try {
        await liveScheduleTick();
      } catch {
        /* swallow — a bad tick must not kill the loop */
      }
      if (!cancelled) {
        setLastTickAt(new Date().toISOString());
        timer = setTimeout(loop, intervalMs);
      }
    };

    loop();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [intervalMs]);

  return { lastTickAt };
}
